import axios from "axios";

import ListingHelper from "../../helpers/Listing.js";

/**
 * ⚠️ ADAPTER — the ONE place the shape of `POST /api/listings/dictate` lives.
 * Everything downstream (overlay, checklist, candidate cards, form seed) reads
 * the normalised shape this file returns, so a contract change is one edit.
 *
 * Request: multipart, ONE file field named exactly `audio`, ≤ 2048 KB.
 * The server sniffs the real mime, so the 16kHz mono WAV from dictation/wav.js
 * is what has to be sent — not a MediaRecorder container renamed to .wav.
 *
 * Response:
 *   {
 *     "usage_id": 812,
 *     "transcript": "I've got a 2021 Rolex Submariner Date…",
 *     "heard":      { "brand": "Rolex", "model": "Submariner Date",
 *                     "reference_number": "126610LN" },   // identity only
 *     "fields":     { "condition": "new", "price": 12000.0, … },  // form keys,
 *                    // ONLY what was heard — a field not heard is ABSENT
 *     "confidence": { "condition": 0.91, "price": 0.62, … },
 *     "missing":    ["availability"],
 *     "candidates": [{ "product_id": 4471, "score": 0.81, "source": "reference",
 *                      "category_id": 9, "brand": "Rolex",
 *                      "brand_id": 1, "model_id": 7,
 *                      "model": "Submariner Date", "reference_number": "126610LN",
 *                      "dial_color": "Black", "case_diameter": "41",
 *                      "case_material": "Stainless Steel", "image_ids": [90123] }],
 *     "top_score": 0.81
 *   }
 *
 * Three traps this file absorbs so nothing else has to:
 *
 *  1. PHP serialises an empty associative array as `[]`, not `{}`. `heard`,
 *     `fields` and `confidence` all arrive as `[]` when nothing was extracted.
 *  2. `top_score` is null whenever the top candidate came from the exact
 *     reference lookup or the SQL search fallback. It is NOT "did we match" —
 *     that question is answered by `candidates.length`.
 *  3. Money (`price`, `buying_price`, `retail_price`) arrives in MAJOR units as
 *     floats. It is passed through untouched: `processSubmitValues` multiplies
 *     by 100 on submit, so converting here would bill 100× the asking price.
 */

/** PHP's empty-array-for-empty-object. Anything not a plain object is "empty". */
const asMap = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const toField = (value, confidence) => {
  if (value === null || value === undefined || value === "") return null;
  return { value, confidence: typeof confidence === "number" ? confidence : null };
};

const toCandidate = (raw, index) => ({
  // product_id can repeat (the exact hit is often also the top vector hit), so
  // the rendered position is part of the key.
  key: `${raw?.product_id ?? "x"}-${index}`,
  id: raw?.product_id ?? null,
  score: typeof raw?.score === "number" ? raw.score : null,
  source: raw?.source ?? "vector",
  // The SQL fallback has no score and no vector standing. It is shown, never
  // suppressed — but never as a peer of a scored match either.
  loose: raw?.source === "search",
  reference: raw?.reference_number ?? "",
  brand: raw?.brand ?? "",
  model: raw?.model ?? "",
  // Ids, once the backend sends them. Until then they are null and the ids are
  // resolved from the names against the catalogue lists — see DictationReview.
  brandId: raw?.brand_id ?? null,
  modelId: raw?.model_id ?? null,
  categoryId: raw?.category_id ?? null,
  // string|null, both of them — rendered only when present.
  caseMaterial: raw?.case_material ?? null,
  caseDiameter: raw?.case_diameter ?? null,
  dialColor: raw?.dial_color ?? null,
  imageId: raw?.image_ids?.[0] ?? null,
  imageIds: Array.isArray(raw?.image_ids) ? raw.image_ids : [],
});

export const normaliseDictation = (body) => {
  const data = body?.data ?? body ?? {};
  const confidence = asMap(data.confidence);

  // `heard` (identity) and `fields` (form values) are two maps server-side; the
  // checklist reads one lookup. Their keys don't overlap — brand / model /
  // reference_number are not listing-form field names.
  const fields = {};
  Object.entries({ ...asMap(data.heard), ...asMap(data.fields) }).forEach(([key, value]) => {
    const field = toField(value, confidence[key]);
    if (field) fields[key] = field;
  });

  const candidates = (Array.isArray(data.candidates) ? data.candidates : [])
    .map(toCandidate)
    .filter((c) => c.id !== null);

  return {
    usageId: data.usage_id ?? null,
    transcript: data.transcript ?? "",
    fields,
    missing: Array.isArray(data.missing) ? data.missing : [],
    // Split for rendering, not for filtering: everything the server returned is
    // shown, the unscored fallback under its own heading.
    candidates: candidates.filter((c) => !c.loose),
    looseCandidates: candidates.filter((c) => c.loose),
  };
};

const ERROR_BY_CODE = {
  dictation_daily_cap: {
    message: "You've hit today's dictation limit. The manual form is unaffected.",
    retryable: false,
  },
  dictation_unavailable: {
    message: "Dictation is unavailable right now. Your recording is kept — try again shortly.",
    retryable: true,
  },
};

/** Turn an axios failure into something the overlay can say out loud. */
const toDictationError = (error) => {
  const status = error?.response?.status;
  const body = error?.response?.data ?? {};
  const known = ERROR_BY_CODE[body.code];
  const wrapped = new Error(
    known?.message ??
      (status === 422
        // Laravel bag, keyed on the upload field.
        ? body.errors?.audio?.[0] ?? "That recording couldn't be used."
        : status === 403
          ? "You don't have permission to create listings here."
          : "Couldn't process that. Your recording is kept, so you can retry.")
  );
  wrapped.userFacing = true;
  // A 401 is the app's normal re-auth path; a 403 or 422 will not improve on a
  // second identical upload.
  wrapped.retryable = known ? known.retryable : !(status === 403 || status === 422);
  wrapped.status = status;
  return wrapped;
};

/**
 * @param {Blob} blob  16kHz mono WAV from src/dictation/wav.js
 */
export async function dictateListing(blob, { mode = "guided", signal } = {}) {
  const form = new FormData();
  form.append("audio", blob, "dictation.wav");
  form.append("mode", mode);

  try {
    const response = await axios.post("/api/listings/dictate", form, {
      signal,
      headers: { "Content-Type": "multipart/form-data" },
    });
    return normaliseDictation(response.data);
  } catch (error) {
    if (error?.response) throw toDictationError(error);
    throw error;
  }
}

/**
 * Re-analyse a CORRECTED transcript. Same endpoint, same response shape — the
 * only difference is that `text` is sent instead of `audio`, so everything
 * downstream (checklist, candidates, form seed) reads the result it already
 * knows how to read.
 *
 * Why it exists: speech-to-text mishears reference numbers and model names
 * before anything else, and those are the fields that decide which product the
 * listing points at. Letting the rep fix the transcript without re-reading it
 * would leave the text and the fields disagreeing, with nothing saying which
 * one the listing was actually built from.
 *
 * @param {string} text the corrected transcript
 */
export async function redictateText(text, { signal } = {}) {
  try {
    const response = await axios.post(
      "/api/listings/dictate",
      { text },
      { signal }
    );
    return normaliseDictation(response.data);
  } catch (error) {
    // A server that predates the `text` input rejects this as a missing `audio`
    // field. Left to the generic handler the user is told "The audio field is
    // required." on a screen with no audio on it — so say what is actually
    // true. This branch stops matching the moment the backend ships.
    if (
      error?.response?.status === 422 &&
      error.response.data?.errors?.audio &&
      !error.response.data?.errors?.text
    ) {
      const stale = new Error(
        "This server can't re-read edited text yet. Your change is kept, but the fields below still come from the recording."
      );
      stale.userFacing = true;
      stale.retryable = false;
      stale.status = 422;
      throw stale;
    }
    if (error?.response) throw toDictationError(error);
    throw error;
  }
}

/**
 * Tell the backend what the human did with the candidates. The score floor
 * ships uncalibrated and is tuned from exactly this, so a dismissal matters as
 * much as an acceptance. Fire-and-forget: a failure here must never block the
 * form from opening.
 *
 * @param {number} usageId
 * @param {{accepted: boolean, productId?: number|null, position?: number|null}} outcome
 *        `position` is 0-based AS RENDERED, across both candidate groups.
 */
export function reportDictationOutcome(usageId, { accepted, productId = null, position = null }) {
  if (!usageId) return Promise.resolve();
  return axios
    .patch(`/api/listings/dictate/${usageId}`, {
      accepted,
      product_id: productId,
      position,
    })
    .catch(() => {});
}

/**
 * Create the listing straight from the dictation sheet — the rep never passes
 * through the create form any more.
 *
 * The payload goes through the form's OWN processSubmitValues so the money
 * conversion happens exactly once and in the same place: every price here is in
 * MAJOR units and is multiplied by 100 there. Duplicating that ×100 is the most
 * expensive bug this feature could ship.
 *
 * @param {object} listing  listing fields in MAJOR units, plus a nested product
 */
export async function createDictatedListing(listing) {
  const { data } = await axios.post(
    "/api/listings",
    ListingHelper.processSubmitValues(listing)
  );
  return data?.data ?? data;
}
